import { useCallback, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

// SegmentedControl — controlled view-mode switch (story 2-139; P10 §2.4).
//
// ── What this is, and is not ────────────────────────────────────────────
// A SegmentedControl changes how the SAME content renders (e.g. rows vs
// cards for one collection) and never owns a route or a distinct content
// panel — unlike `Tabs`, which switches between DIFFERENT panels and owns
// the WAI-ARIA tablist/tabpanel pair (`aria-controls` pointing at a panel
// this same primitive renders). So this primitive renders ONLY the switch:
// no panel, no owned content region. The caller decides what "view" means
// and renders it themselves, keyed off `value` — same divide as a native
// `<select>` versus the thing it controls.
//
// ── The a11y contract (WAI-ARIA "radio group" pattern, not tablist) ───────
// A segmented control is a set of MUTUALLY EXCLUSIVE toggle buttons — the
// APG's radio-group pattern: `role="radiogroup"` wrapping `role="radio"`
// buttons, `aria-checked` (not `aria-selected` — that vocabulary belongs to
// tabs/options, this is a checked STATE), roving tabindex (exactly one
// `tabIndex=0`, the rest `-1`, so Tab moves into/out of the control rather
// than through every option), and arrow keys that both MOVE and SELECT
// (activation follows focus — safe here because switching a view mode is an
// instant client-side re-render over data already on screen, never a
// fetch). Home/End jump to the ends. This is the same keyboard SHAPE `Tabs`
// already established, applied to the radio-group role instead of tablist —
// see that file's header for why the pattern (not the ARIA role) repeats
// rather than sharing code: the two primitives answer different semantic
// questions ("which panel" vs "which rendering of this one collection") and
// a shared implementation would blur that on the very first prop.
//
// ── CONTROLLED ONLY ──────────────────────────────────────────────────────
// Same reasoning as `Tabs`: a collection page keeps its view mode in URL
// state (G-25 — reload/back must restore it), so a primitive that owned the
// value internally would either fight the URL or need a second mode. One
// mode, so there is exactly one source of truth.

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Decorative icon shown before the label (compact icon+label option, P10 §2.4). */
  icon?: ReactNode;
  disabled?: boolean;
  /** Overrides the accessible name for this option (defaults to `label`'s text content). */
  'aria-label'?: string;
}

type Size = 'sm' | 'md';

// Story 2-147qa — at HEAD this primitive had no `size` prop at all: one
// hardcoded option height (`px-2.5 py-1.5 text-sm`, ~32px), a FOURTH value
// alongside Button/IconButton's 28/36px and Input/Select's pre-convergence
// ~24/28px. Options now sit on the same shared control-height scale
// (tokens.css `--control-h-sm`/`--control-h-md`) every other control on
// this page converges to. `md` (36px) is the new default — every existing
// call site (ItemsListPage/SitesPage/TemplateEditorPage, none pass `size`)
// grows by 4px; `sm` (28px) is additive, opt-in, matching Button/Input's
// own `sm`.
const OPTION_SIZE_CLASS: Record<Size, string> = {
  sm: 'h-control-sm px-2 text-label',
  md: 'h-control px-2.5 text-sm',
};

export interface SegmentedControlProps<T extends string> {
  options: Array<SegmentedOption<T>>;
  /** The selected option's value. Controlled — see the header. */
  value: T;
  onChange: (next: T) => void;
  /** Names the control for screen readers. Required: an unlabelled radiogroup
   * announces as "radio group" and nothing else. */
  'aria-label': string;
  /** Matches `Button`/`Input`'s own size scale (story 2-147qa). @default 'md' */
  size?: Size;
  className?: string;
}

export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>): JSX.Element {
  const { options, value, onChange, size = 'md', className = '' } = props;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const selectable = options.filter((o) => !o.disabled);

  const move = useCallback(
    (delta: number) => {
      if (selectable.length === 0) return;
      const current = selectable.findIndex((o) => o.value === value);
      // `+ length` before the modulo so a backwards step from index 0 wraps
      // to the end instead of producing -1 (same trick `Tabs` uses).
      const next = selectable[(current + delta + selectable.length) % selectable.length];
      if (!next) return;
      onChange(next.value);
      refs.current[String(next.value)]?.focus();
    },
    [onChange, selectable, value],
  );

  const jump = useCallback(
    (edge: 'first' | 'last') => {
      const next = edge === 'first' ? selectable[0] : selectable[selectable.length - 1];
      if (!next) return;
      onChange(next.value);
      refs.current[String(next.value)]?.focus();
    },
    [onChange, selectable],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        event.preventDefault();
        jump('first');
        break;
      case 'End':
        event.preventDefault();
        jump('last');
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={props['aria-label']}
      onKeyDown={onKeyDown}
      // `max-w-full overflow-x-auto` — story 2-147c: found live at 390px that
      // a plain `inline-flex` radiogroup with more options than fit the
      // viewport just grew PAST it (no max-width to respect), pushing the
      // whole control off-screen with no scrollbar — worse inside a
      // `.glass-card` ancestor (tokens.css's own `overflow: hidden`), which
      // silently clips the overflow instead of showing it. `max-w-full`
      // caps the control at its container's width so `overflow-x-auto` has
      // something to actually scroll; `shrink-0` on each option (below)
      // keeps every option at its natural width rather than being squeezed.
      className={`inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-border bg-muted/40 p-0.5 ${className}`}
    >
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            // `type="button"` is mandatory — an untyped <button> inside a
            // <form> defaults to submit (same trap `Switch`/`Tabs` document).
            type="button"
            role="radio"
            aria-checked={checked}
            {...(option['aria-label'] ? { 'aria-label': option['aria-label'] } : {})}
            disabled={option.disabled}
            // The roving tabindex — see the header.
            tabIndex={checked ? 0 : -1}
            ref={(node) => {
              refs.current[option.value] = node;
            }}
            onClick={() => onChange(option.value)}
            className={`focus-ring inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 ${OPTION_SIZE_CLASS[size]} ${
              checked ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
